import { Outlet, NavLink, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LayoutDashboard, ImageIcon, CalendarDays, MessageSquare, Crown, LogOut, Sparkles, Gift, Settings, Users, Instagram } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

export default function PortalLayout() {
    const { user, logout } = useAuth();
    const [sub, setSub] = useState(null);

    const fetchSub = useCallback(
        () => api.get("/subscription/status").then((r) => setSub(r.data)).catch((err) => console.warn("sub fetch failed:", err?.message || err)),
        []
    );

    useEffect(() => {
        fetchSub();
    }, [fetchSub]);

    const links = [
        { to: "/portal", icon: LayoutDashboard, label: "Dashboard", end: true },
        { to: "/portal/gallery", icon: ImageIcon, label: "Gallery" },
        { to: "/portal/book", icon: CalendarDays, label: "Book" },
        { to: "/portal/messages", icon: MessageSquare, label: "Messages" },
        { to: "/portal/subscription", icon: Crown, label: "Subscription" },
        { to: "/portal/referrals", icon: Gift, label: "Friend Pass" },
        ...(user?.role === "admin"
            ? [
                { to: "/portal/admin/leads", icon: Users, label: "Leads" },
                { to: "/portal/admin/instagram", icon: Instagram, label: "IG Captions" },
                { to: "/portal/admin/services", icon: Settings, label: "Service Menu" },
            ]
            : []),
    ];

    const outletCtx = useMemo(() => ({ sub, refreshSub: fetchSub }), [sub, fetchSub]);

    return (
        <div className="min-h-screen bg-ink text-white flex">
            {/* Sidebar */}
            <aside className="hidden lg:flex w-72 shrink-0 flex-col border-r border-hairline bg-surface/60 backdrop-blur-xl">
                <div className="p-6 border-b border-hairline">
                    <Link to="/" className="flex items-center gap-3" data-testid="portal-logo">
                        <img src="/brand/24k-logo.jpg" alt="24K Glow" className="w-8 h-8 rounded-full object-cover ring-1 ring-gold/40" />
                        <span className="font-serif text-base tracking-[0.18em] uppercase"><span className="text-gold">24K</span> Glow</span>
                    </Link>
                </div>
                <div className="p-6 border-b border-hairline">
                    <div className="flex items-center gap-3">
                        <img
                            src={user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || user?.email || "Glow")}&background=E8A5B9&color=0B070D`}
                            alt=""
                            className="w-12 h-12 rounded-full border border-rose/40"
                        />
                        <div className="min-w-0">
                            <p className="font-serif text-lg truncate">{user?.name || "Beautiful you"}</p>
                            <p className="text-xs text-white/50 truncate">{user?.email}</p>
                        </div>
                    </div>
                    <div className="mt-4 p-3 rounded-xl border border-rose/30 bg-rose/5">
                        <p className="text-[10px] tracking-[0.25em] uppercase text-rose">Status</p>
                        <p className="font-serif text-xl mt-1 capitalize">
                            {sub?.status || "—"} {sub?.plan ? <span className="text-white/60 text-sm">· {sub.plan}</span> : null}
                        </p>
                        <p className="text-xs text-white/60 mt-1">{sub?.days_remaining ?? 0} days remaining</p>
                    </div>
                </div>
                <nav className="flex-1 p-4 space-y-1">
                    {links.map(({ to, icon: Icon, label, end }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={end}
                            data-testid={`portal-nav-${label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-3 rounded-xl transition ${
                                    isActive ? "bg-rose/15 text-rose" : "text-white/70 hover:bg-white/5 hover:text-white"
                                }`
                            }
                        >
                            <Icon className="w-4 h-4" />
                            <span>{label}</span>
                        </NavLink>
                    ))}
                </nav>
                <div className="p-4 border-t border-hairline">
                    <button
                        data-testid="portal-logout-btn"
                        onClick={logout}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-white/70 hover:bg-white/5 hover:text-rose transition"
                    >
                        <LogOut className="w-4 h-4" />
                        <span>Sign out</span>
                    </button>
                </div>
            </aside>

            {/* Mobile top bar */}
            <div className="lg:hidden fixed top-0 left-0 right-0 z-30 border-b border-hairline bg-ink/90 backdrop-blur-xl">
                <div className="flex items-center justify-between px-4 h-14">
                    <Link to="/" className="flex items-center gap-2">
                        <img src="/brand/24k-logo.jpg" alt="24K Glow" className="w-7 h-7 rounded-full object-cover ring-1 ring-gold/40" />
                        <span className="font-serif tracking-[0.15em] uppercase text-sm"><span className="text-gold">24K</span> Glow</span>
                    </Link>
                    <button onClick={logout} data-testid="portal-mobile-logout" className="text-white/70 text-sm">Sign out</button>
                </div>
                <div className="flex overflow-x-auto luxe-scroll border-t border-hairline">
                    {links.map(({ to, icon: Icon, label, end }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={end}
                            data-testid={`portal-mobile-nav-${label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `flex items-center gap-2 px-4 py-3 text-xs whitespace-nowrap ${
                                    isActive ? "text-rose border-b-2 border-rose" : "text-white/60"
                                }`
                            }
                        >
                            <Icon className="w-3.5 h-3.5" /> {label}
                        </NavLink>
                    ))}
                </div>
            </div>

            <main className="flex-1 lg:p-10 p-4 pt-32 lg:pt-10 bg-ink">
                <Outlet context={outletCtx} />
            </main>
        </div>
    );
}
